import React, { useEffect, useRef, useState } from 'react';
import { RotationTree, RotationTreeWrapper, EmptyRotationMessage } from './RotationView.styles';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowageStore } from 'src/context/StowageContext';
import { calculateRotationCallStatuses } from 'src/utils/stowage/stowageOperation/rotationCallStatusesUtils';
import StowageRotationTreePortCallItem from './StowageRotationTreePortCallItem';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { sortStowagePlanRotationPortCalls } from 'src/factory/Stowage/StowageOperationFactory';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { getCurrentPortCall, getDefaultStowageRotationTree } from 'src/utils/stowage/stowageRotationUtils';
import { onDragoverParent, onPortCallDragEnd, onPortCallDragOver, onPortCallDragStart } from 'src/utils/stowage/dragAndDrop/dragAndDropPortCallUtils';
import { useStowageUrlChangeHandler } from 'src/hooks/stowage/urlHandler/useStowageUrlChangeHandler';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import { UUID } from 'crypto';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { useAuthStore } from 'src/context/AuthContext';
import { onPortCallDrop } from 'src/utils/stowage/dragAndDrop/dragAndDropCargoOperationUtils';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { deepCopy } from 'src/utils/objectUtils';
import { useVesselStore } from 'src/context/VesselContext';

const StowageRotationTreeComponent: React.FC = () => {
    const { stowageRotationTree, selectedStowageVoyages, selectedStowageRotationPortCall, setSelectedStowageVoyages } = useRotationStore();
    const { activeStowagePlan, setRotationCallStatuses, manifest, isStowagePlanUpdated } = useStowageStore();
    const { role } = useAuthStore();
    const { vessel } = useVesselStore();
    const [filteredStowageRotationTree, setFilteredStowageRotationTree] = useState<RotationPortCall[]>([]);
    const [openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids] = useState<UUID[]>([]);
    const draggoverElement = useRef<HTMLDivElement>(null);
    const dragoverPortCall = useRef<RotationPortCall>(null);
    const dragStartPortCall = useRef<RotationPortCall>(null);
    const { saveStowageData } = usePostStowageApi();
    const { triggerQueryParamUpdate, queryParamUpdate, getQueryParamValueFromUrl } = useQueryParamsHandler();
    const { handleStowageCallQueryParamChanges } = useStowageUrlChangeHandler();
    const { setQueryParamStowagePortCall } = useStowageQueryParamsUtils();

    useEffect(() => {
        if (!isStowagePlanUpdated || !activeStowagePlan || !activeStowagePlan?.rotation || !manifest) {
            return;
        }

        const _rotationCallStatuses = calculateRotationCallStatuses(activeStowagePlan, vessel.firstVoyageOrcaWebStowageImosSync);
        setRotationCallStatuses(_rotationCallStatuses);
        filterStowageRotationTree();
    }, [stowageRotationTree, activeStowagePlan, manifest, selectedStowageVoyages, isStowagePlanUpdated]);

    useEffect(() => {
        if (!queryParamUpdate?.queryParamValues || filteredStowageRotationTree.length === 0) return;
        handleStowageCallQueryParamChanges(queryParamUpdate, filteredStowageRotationTree);
    }, [queryParamUpdate, filteredStowageRotationTree]);

    const filterStowageRotationTree = () => {
        let _filteredStowageRotationTree = null;
        const portCallGuid = getQueryParamValueFromUrl('portCallGuid');

        if (selectedStowageVoyages && selectedStowageVoyages.length > 0) {
            _filteredStowageRotationTree = activeStowagePlan.rotation.filter(
                (pc) =>
                    selectedStowageVoyages.includes(pc.voyageNumber) ||
                    (pc.turnaroundVoyageNumber && selectedStowageVoyages.includes(pc.turnaroundVoyageNumber))
            );

            // If no port calls match the selected voyages, reset to default selection
            if (_filteredStowageRotationTree.length === 0) {
                _filteredStowageRotationTree = getDefaultStowageRotationTree(activeStowagePlan);
                setFilteredStowageRotationTree([..._filteredStowageRotationTree]);

                const _selectedVoyages = Array.from(new Set(_filteredStowageRotationTree.map((portCall) => portCall.voyageNumber)))
                    .toString()
                    .split(',');
                setSelectedStowageVoyages(_selectedVoyages);

                const _currentPortCall = getCurrentPortCall(activeStowagePlan);
                if (_currentPortCall) {
                    setQueryParamStowagePortCall(_currentPortCall.portCallGuid);
                }
                return;
            }

            setFilteredStowageRotationTree(_filteredStowageRotationTree);
            const _selectedPortCall = activeStowagePlan.rotation.filter(
                (pc) => selectedStowageVoyages.includes(pc.voyageNumber) && pc?.portCallGuid === selectedStowageRotationPortCall?.portCallGuid
            );
            if (!_selectedPortCall) {
                const _newSelectedPortCall = activeStowagePlan.rotation.find((pc) => selectedStowageVoyages.includes(pc.voyageNumber)) || null;
                triggerQueryParamUpdate({
                    name: 'portCallGuid',
                    newValue: _newSelectedPortCall?.portCallGuid || null,
                    clearParam: !_newSelectedPortCall,
                });
            }
        } else if (!portCallGuid) {
            _filteredStowageRotationTree = getDefaultStowageRotationTree(activeStowagePlan);
            setFilteredStowageRotationTree([..._filteredStowageRotationTree]);

            const _selectedVoyages = Array.from(new Set(_filteredStowageRotationTree.map((portCall) => portCall.voyageNumber)))
                .toString()
                .split(',');
            setSelectedStowageVoyages(_selectedVoyages);
        } else if (portCallGuid) {
            const _selectedVoyageNumber = activeStowagePlan.rotation.find((pc) => pc?.portCallGuid === portCallGuid)?.voyageNumber;
            setSelectedStowageVoyages([_selectedVoyageNumber]);

            _filteredStowageRotationTree = activeStowagePlan.rotation.filter((pc) => pc.voyageNumber === _selectedVoyageNumber);
            setFilteredStowageRotationTree(_filteredStowageRotationTree);
        }

        if (!portCallGuid) {
            const _currentPortCall = getCurrentPortCall(activeStowagePlan);

            if (_currentPortCall) {
                setQueryParamStowagePortCall(_currentPortCall.portCallGuid);
            }
        } else if (
            portCallGuid &&
            !_filteredStowageRotationTree.find((p) => p?.portCallGuid === portCallGuid) &&
            _filteredStowageRotationTree.length > 0
        ) {
            const _newSelectedPortCall = _filteredStowageRotationTree[0];
            setQueryParamStowagePortCall(_newSelectedPortCall.portCallGuid);
        }
    };

    const onDragEnd = (event: React.DragEvent<HTMLDivElement>, portCallItem: RotationPortCall) => {
        const stowagePlanChanges = onPortCallDragEnd(
            event,
            portCallItem,
            activeStowagePlan.stowagePlanGuid,
            deepCopy(filteredStowageRotationTree),
            dragStartPortCall,
            draggoverElement,
            dragoverPortCall
        );
        if (stowagePlanChanges.length > 0) {
            saveStowageData(activeStowagePlan, stowagePlanChanges);
        }
    };

    const canDragRotationItem = (portCallItem: RotationPortCall) => {
        if (portCallItem.portCallStatus === PortCallStatusEnum.Departed || !hasPermission(role, stowageFeatureEnum.EditStowage)) {
            return false;
        }

        return true;
    };

    const handleOnDrop = (e: React.DragEvent, portCallItem: RotationPortCall) => {
        e.preventDefault();
        e.stopPropagation();

        const _stowagePlan = deepCopy(activeStowagePlan);
        const stowagePlanChanges = onPortCallDrop(e, portCallItem, filteredStowageRotationTree, _stowagePlan);

        if (stowagePlanChanges.length > 0) {
            saveStowageData(_stowagePlan, stowagePlanChanges);
        }
    };
    return (
        <RotationTreeWrapper>
            <RotationTree>
                {filteredStowageRotationTree && filteredStowageRotationTree.length > 0 ? (
                    sortStowagePlanRotationPortCalls(filteredStowageRotationTree).map((portCallItem) => {
                        return (
                            <div
                                key={`port-call-item-${portCallItem.portCallGuid}`}
                                onDragOver={(e) => onDragoverParent(e, portCallItem, dragStartPortCall)}
                            >
                                <div
                                    className="dragging-parent"
                                    draggable={canDragRotationItem(portCallItem)}
                                    onDragStart={(e) => onPortCallDragStart(e, portCallItem, dragStartPortCall)}
                                    onDragEnd={(e) => onDragEnd(e, portCallItem)}
                                    onDragOver={(e) => onPortCallDragOver(e, portCallItem, dragStartPortCall, draggoverElement, dragoverPortCall)}
                                    onDrop={(e) => handleOnDrop(e, portCallItem)}
                                >
                                    <StowageRotationTreePortCallItem
                                        openPortCallMenuItemGuids={openPortCallMenuItemGuids}
                                        setOpenPortCallMenuItemGuids={setOpenPortCallMenuItemGuids}
                                        portCall={portCallItem}
                                        filteredStowageRotationTree={filteredStowageRotationTree}
                                    />
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <EmptyRotationMessage>No rotation data available</EmptyRotationMessage>
                )}
            </RotationTree>
        </RotationTreeWrapper>
    );
};

export default StowageRotationTreeComponent;
